/**
 * Cross-platform microphone access helper.
 *
 * Why this exists:
 *   On Android Capacitor WebView, ``navigator.mediaDevices.getUserMedia``
 *   often fails silently when the JS-level permission request isn't
 *   forwarded to the OS. Our MainActivity now grants WebView permissions
 *   automatically (see android/.../MainActivity.java), but we still need
 *   to:
 *     1. Ask Android for the OS-level ``RECORD_AUDIO`` runtime permission
 *        BEFORE calling getUserMedia (the OS-level dialog must be granted).
 *     2. On iOS Capacitor, trigger ``getUserMedia`` from a real user gesture
 *        so the OS shows the mic prompt at the right moment.
 *     3. Surface clean error messages so the UI can prompt re-try / settings.
 *
 * Returns a ``MediaStream`` ready to feed into MediaRecorder, or throws a
 * typed ``MicError`` describing the failure.
 */
import { Capacitor } from '@capacitor/core';

const isNative = () => Capacitor.isNativePlatform?.();

export class MicError extends Error {
  constructor(kind, message) {
    super(message);
    this.kind = kind; // 'denied' | 'unavailable' | 'unsupported' | 'insecure'
  }
}

async function requestNativeAudioPermission() {
  if (!isNative()) return 'granted';
  // On native platforms (Android/iOS), permissions are handled via:
  // • Android: MainActivity.java requests RECORD_AUDIO + MODIFY_AUDIO_SETTINGS at runtime
  // • iOS: NSMicrophoneUsageDescription in Info.plist triggers OS dialog on first getUserMedia
  // We proceed to getUserMedia which will trigger the native permission dialog if needed.
  return 'prompt';
}

export async function acquireMicrophoneStream({ constraints, signal } = {}) {
  // 1) Secure context check
  if (typeof window !== 'undefined'
      && window.isSecureContext === false
      && !isNative()) {
    throw new MicError('insecure',
      "L'enregistrement nécessite une connexion sécurisée (https). Réessayez via https://app.noorelbayen.com.");
  }
  // 2) Browser support check
  if (typeof navigator === 'undefined'
      || !navigator.mediaDevices
      || !navigator.mediaDevices.getUserMedia) {
    throw new MicError('unsupported',
      "Votre appareil ne supporte pas l'enregistrement audio dans le navigateur.");
  }
  // 3) Native pre-flight
  await requestNativeAudioPermission();

  // 4) Acquire stream — handle abort
  const audioConstraints = constraints || {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    sampleRate: 44100,
    channelCount: 1,
  };
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    if (signal?.aborted) {
      stream.getTracks().forEach((t) => t.stop());
      throw new MicError('aborted', 'Annulé');
    }
    return stream;
  } catch (err) {
    const name = err?.name || '';
    if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
      throw new MicError('denied',
        "Permission microphone refusée. Activez-la dans les réglages de votre appareil ou navigateur, puis réessayez.");
    }
    if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
      throw new MicError('unavailable',
        "Aucun microphone détecté sur votre appareil.");
    }
    if (name === 'NotReadableError' || name === 'TrackStartError') {
      throw new MicError('unavailable',
        "Le microphone est déjà utilisé par une autre application. Fermez-la puis réessayez.");
    }
    throw new MicError('unavailable', err?.message || 'Erreur audio inconnue');
  }
}

/** Pick the best MIME type the platform supports. */
export function pickRecorderMime() {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/aac',
    'audio/x-m4a',
  ];
  for (const mt of candidates) {
    try {
      if (MediaRecorder.isTypeSupported?.(mt)) return mt;
    } catch { /* ignore */ }
  }
  return null;
}
